// [PURPOSE]: Single source of truth for the shape of every Firebase RTDB
//            party write. Each helper attaches the UID-ownership fields
//            (ownerUid / actorUid / addedByUid / dmUid) that the v1.0 RTDB
//            security rules use to enforce "only owner can edit own subtree."
// [WHY]: Phase 2 audit (S-2) found party records were keyed by characterId
//        but Firebase rules can only check auth.uid. Without an explicit
//        owner-UID field on each record, rules can't enforce isolation.
//        These helpers make the schema authoritative; rules enforce it.
// [INTEGRATION]: Wired in src/components/SettingsPanel.jsx (handleCreateParty,
//        handleJoin) and src/hooks/useEnhancedPartySync.js (syncActivity,
//        addToSharedVault).

const PARTY_SCHEMA_VERSION = 1;

const DEFAULT_HP = 10;

function defaultClock() {
  const now = Date.now();
  return {
    iso: () => new Date(now).toISOString(),
    ms: () => now,
  };
}

function ensureAuthUid(authUid, where) {
  if (!authUid || typeof authUid !== 'string') {
    throw new Error(`[partyWriteHelpers] ${where} requires authUid (got ${authUid})`);
  }
}

function ensureCharacterId(characterId, where) {
  // [WHY]: characterId flows into metadata.dmId / members[$id] keys. Firebase RTDB
  // rejects writes containing `undefined`, so a missing characterId surfaced as the
  // opaque "value argument contains undefined in property 'parties.X.metadata.dmId'"
  // error. Throw early with the call-site name so the bug is obvious in logs.
  if (!characterId || (typeof characterId !== 'string' && typeof characterId !== 'number')) {
    throw new Error(`[partyWriteHelpers] ${where} requires characterId (got ${characterId})`);
  }
}

/**
 * [PHASE B / #002]: Recursively strip `undefined` from a value before it
 * reaches Firebase RTDB. RTDB rejects writes containing `undefined`
 * anywhere in the value tree with the opaque "value argument contains
 * undefined in property '...'" error.
 *
 * The May 5 smoke test surfaced this on the activity-feed path:
 *
 *   set failed: value argument contains undefined in property
 *   'parties.testpart1.activityFeed.<key>.details.weight'
 *
 * The activity-feed payload spread in `details` verbatim from
 * `logActivity(message, type, details)` callers, several of which pass
 * `weight: item.weight` directly — and `item.weight` is `undefined`
 * for some custom / legacy items. Pruning at the helper boundary keeps
 * the fix in ONE place rather than chasing every call site.
 *
 * Behavior:
 *   - Objects: copy with every `undefined` key omitted; recurse on values.
 *   - Arrays: copy with every `undefined` element omitted; recurse on the rest.
 *   - Primitives (string, number, boolean), `null`, and other values pass through.
 *   - `undefined` itself returns `undefined` so the caller (the parent
 *     pruner) knows to drop the key/element.
 *
 * Falsy-but-valid values (`0`, `''`, `false`, `null`) are preserved —
 * RTDB accepts all of them.
 *
 * [PSR-010 WARNING]: `undefined` always means **skip / omit**, never **delete**.
 *
 *   - Object keys: the key is dropped entirely from the output — the field
 *     will NOT be written, so if it already exists in RTDB it stays unchanged.
 *   - Array elements: the element is omitted and remaining elements shift left,
 *     changing their indices.  Callers MUST NOT rely on positional array access
 *     after a round-trip through this function when any element may be undefined.
 *
 * If a caller wants Firebase to DELETE an existing path, it must pass `null`
 * explicitly — RTDB interprets `set(ref, null)` as deletion. Passing
 * `undefined` here just silently skips the field, leaving the old value in place.
 */
function pruneUndefinedDeep(value) {
  if (value === undefined) return undefined;
  if (value === null) return null;
  if (Array.isArray(value)) {
    const out = [];
    for (const v of value) {
      const cleaned = pruneUndefinedDeep(v);
      if (cleaned !== undefined) out.push(cleaned);
    }
    return out;
  }
  if (typeof value === 'object') {
    const out = {};
    for (const [k, v] of Object.entries(value)) {
      const cleaned = pruneUndefinedDeep(v);
      if (cleaned !== undefined) out[k] = cleaned;
    }
    return out;
  }
  return value;
}

/**
 * Build the full payload for `set(ref(db, parties/$partyId), <payload>)` when
 * a DM creates a new party.
 *
 * @param {object} input
 * @param {string|null} input.authUid - DM's Firebase auth UID (may be null;
 *   memberUids is omitted in that case)
 * @param {string} input.characterId - DM's character ID (the key under members/)
 * @param {string} input.characterName - DM's character display name
 * @param {number} [input.hitPoints]
 * @param {number} [input.maxHitPoints]
 * @param {string} input.partyDisplayName
 * @param {string} [input.partyName] - User-supplied display name from the
 *   create-party splash form (spec 2026-05-11). Preferred over partyDisplayName
 *   for the root `name` field; also written to metadata.partyName.
 * @param {string} [input.passwordHash] - Pre-hashed password (PSR-005 fix —
 *   caller hashes via hashPartyPassword(plaintext, partyId) before calling).
 *   Blank = open party. Stored as metadata.passwordHash.
 * @param {{iso: () => string, ms: () => number}} [input.clock]
 */
function buildCreatePartyPayload({
  authUid,
  characterId,
  characterName,
  hitPoints,
  maxHitPoints,
  partyDisplayName,
  partyName,        // NEW: user-supplied display name from the splash form
  passwordHash = '',
  clock = defaultClock(),
}) {
  ensureCharacterId(characterId, 'buildCreatePartyPayload');
  const iso = clock.iso();
  const ms = clock.ms();
  const hp = typeof hitPoints === 'number' ? hitPoints : DEFAULT_HP;
  const maxHp = typeof maxHitPoints === 'number' ? maxHitPoints : DEFAULT_HP;
  const trimmedHash = (passwordHash || '').trim();
  // [SPEC 2026-05-11]: partyName is the user-supplied display name from the new
  //   create-splash form. Prefer it over the auto-synthesized partyDisplayName.
  //   Both are written: root `name` stays in sync for back-compat readers,
  //   and metadata.partyName is the canonical new home.
  const trimmedPartyName = typeof partyName === 'string' ? partyName.trim() : '';
  const effectiveDisplay = trimmedPartyName || partyDisplayName;

  const payload = {
    name: effectiveDisplay,
    createdAt: iso,
    metadata: {
      schemaVersion: PARTY_SCHEMA_VERSION,
      dmId: characterId,
      dmUid: authUid || null,
      creatorId: characterId,
      creatorUid: authUid || null,
      createdAt: iso,
      ...(trimmedPartyName ? { partyName: trimmedPartyName } : {}),
      // [PSR-005 FIX — MAY 2026]: Store passwordHash, not plaintext.
      //   Caller pre-hashes via hashPartyPassword(plaintext, partyId).
      //   Blank = open party (omit entirely).
      ...(trimmedHash ? { passwordHash: trimmedHash } : {}),
    },
    members: {
      [characterId]: {
        ownerUid: authUid || null,
        characterName: characterName || 'Unknown',
        role: 'dm',
        status: 'online',
        hp,
        maxHp,
        joinedAt: iso,
        lastSeenAt: ms,
      },
    },
    memberUids: authUid ? { [authUid]: true } : {},
  };

  return pruneUndefinedDeep(payload);
}

/**
 * Build the payload for `set(ref(db, parties/$partyId/members/$characterId), <payload>)`
 * when a player joins an existing party.
 */
function buildJoinPartyMemberPayload({
  authUid,
  characterId,
  characterName,
  hitPoints,
  maxHitPoints,
  clock = defaultClock(),
}) {
  ensureAuthUid(authUid, 'buildJoinPartyMemberPayload');
  ensureCharacterId(characterId, 'buildJoinPartyMemberPayload');
  const iso = clock.iso();
  const ms = clock.ms();
  const hp = typeof hitPoints === 'number' ? hitPoints : DEFAULT_HP;
  const maxHp = typeof maxHitPoints === 'number' ? maxHitPoints : DEFAULT_HP;

  return pruneUndefinedDeep({
    ownerUid: authUid,
    characterId,
    characterName: characterName || 'Unknown',
    role: 'player',
    status: 'online',
    hp,
    maxHp,
    joinedAt: iso,
    lastSeenAt: ms,
  });
}

/**
 * Build the payload for a new activity-feed entry under
 * `parties/$partyId/activityFeed/$pushKey`.
 */
function buildActivityFeedEntry({
  authUid,
  characterId,
  characterName,
  activityEntry,
  clock = defaultClock(),
}) {
  ensureAuthUid(authUid, 'buildActivityFeedEntry');
  const iso = clock.iso();

  return pruneUndefinedDeep({
    ...activityEntry,
    actorUid: authUid,
    characterId: characterId || activityEntry?.characterId || null,
    characterName: characterName || activityEntry?.characterName || 'Unknown',
    synced: true,
    timestamp: activityEntry?.timestamp || iso,
  });
}

/**
 * Build the payload for a new shared-vault entry under
 * `parties/$partyId/sharedVault/$pushKey`.
 */
function buildSharedVaultEntry({
  authUid,
  characterId,
  characterName,
  item,
  itemId,
  clock = defaultClock(),
}) {
  ensureAuthUid(authUid, 'buildSharedVaultEntry');
  const iso = clock.iso();

  return pruneUndefinedDeep({
    item: { ...item, id: itemId },
    addedBy: characterId || null,
    addedByUid: authUid,
    addedByName: characterName || 'Unknown',
    addedAt: iso,
    claimedBy: null,
  });
}

/**
 * [BATCH 1.5 — S-5]: Build the payload for an active-effect entry under
 * `parties/$partyId/activeEffects/$targetCharacterId/$effectId`. Stamps
 * `casterUid` so the rule can verify auth.uid owns the cast.
 */
function buildActiveEffectEntry({
  authUid,
  casterCharacterId,
  casterCharacterName,
  targetCharacterId,
  effect,
  clock = defaultClock(),
}) {
  ensureAuthUid(authUid, 'buildActiveEffectEntry');
  const iso = clock.iso();

  return pruneUndefinedDeep({
    ...effect,
    casterUid: authUid,
    casterCharacterId: casterCharacterId || null,
    casterCharacterName: casterCharacterName || 'Unknown',
    targetCharacterId: targetCharacterId || null,
    appliedAt: iso,
  });
}

/**
 * [BATCH 1.5 — S-4]: Build the payload for a DM-pushed content entry under
 * `parties/$partyId/pushedContent/{shop|note|calendarEvent|lootRoll}/$pushKey`.
 * Stamps `pushedByUid` for parity with other UID-tracked entries; the rule
 * itself enforces "DM-only" via metadata.dmUid match.
 */
function buildPushedContentEntry({
  authUid,
  dmCharacterId,
  dmCharacterName,
  contentKind,
  content,
  clock = defaultClock(),
}) {
  ensureAuthUid(authUid, 'buildPushedContentEntry');
  const iso = clock.iso();

  return pruneUndefinedDeep({
    pushedByUid: authUid,
    pushedBy: dmCharacterId || null,
    pushedByName: dmCharacterName || 'Unknown',
    kind: contentKind,
    content,
    pushedAt: iso,
  });
}

export {
  PARTY_SCHEMA_VERSION,
  buildCreatePartyPayload,
  buildJoinPartyMemberPayload,
  buildActivityFeedEntry,
  buildSharedVaultEntry,
  buildActiveEffectEntry,
  buildPushedContentEntry,
  pruneUndefinedDeep,
};
